import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";

// Silence the harmless "ResizeObserver loop completed with undelivered
// notifications" warning. Chrome fires this when a ResizeObserver callback
// mutates layout that triggers another observation in the same frame — the
// browser recovers transparently, but CRA's dev error overlay (and some
// monitors) treat it as an uncaught error. We swallow it at the window level
// so the overlay and console stay clean. Real errors from user code are
// unaffected because we only match this exact message.
const isResizeObserverLoopError = (message) =>
  typeof message === "string" &&
  (message.includes("ResizeObserver loop completed with undelivered notifications") ||
    message.includes("ResizeObserver loop limit exceeded"));

// Capture-phase listener so we run BEFORE CRA's react-error-overlay
// listener (which is installed in bubble phase). stopImmediatePropagation
// then prevents the overlay handler from ever firing.
window.addEventListener(
  "error",
  (e) => {
    if (isResizeObserverLoopError(e.message)) {
      e.stopImmediatePropagation();
      e.preventDefault();
    }
  },
  true,
);

window.addEventListener(
  "unhandledrejection",
  (e) => {
    const msg = e.reason?.message || e.reason;
    if (isResizeObserverLoopError(typeof msg === "string" ? msg : "")) {
      e.stopImmediatePropagation();
      e.preventDefault();
    }
  },
  true,
);

// Belt-and-braces: CRA's overlay also surfaces console.error output in
// some configurations. Filter the same message at the console layer.
const _origConsoleError = console.error;
console.error = (...args) => {
  if (args.length && isResizeObserverLoopError(String(args[0]))) return;
  _origConsoleError.apply(console, args);
};

// Source-level fix: patch ResizeObserver to defer callback dispatch via
// requestAnimationFrame. This batches observations into one frame so the
// browser never has to fire the "loop completed with undelivered
// notifications" warning that Chrome emits when a callback synchronously
// mutates layout. The warning is benign but webpack-dev-server's HMR
// overlay installs its own `error` listener that bypasses the
// stopImmediatePropagation above, so blocking it at the source is the
// only reliable fix in dev mode. Radix Select dropdowns inside scroll
// containers are the usual trigger.
if (typeof window !== "undefined" && window.ResizeObserver) {
  const NativeRO = window.ResizeObserver;
  window.ResizeObserver = class DebouncedResizeObserver extends NativeRO {
    constructor(callback) {
      let frame = 0;
      let pending = [];
      let pendingObserver = null;
      const flush = () => {
        frame = 0;
        const entries = pending;
        const observer = pendingObserver;
        pending = [];
        pendingObserver = null;
        try {
          callback(entries, observer);
        } catch (err) {
          if (!isResizeObserverLoopError(err?.message || "")) throw err;
        }
      };
      super((entries, observer) => {
        pending = entries;
        pendingObserver = observer;
        if (!frame) frame = window.requestAnimationFrame(flush);
      });
    }
  };
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
